/**
 * Store Locator - Stanley 1913 AR
 *
 * Lógica del mapa interactivo de tiendas:
 *  - Inicializa Leaflet con tiles de OpenStreetMap
 *  - Crea markers a partir de los <li data-store> renderizados por Liquid
 *  - Maneja tabs (oficial / revendedor), filtro por provincia y panel de detalle
 *  - Panel lateral en desktop (push del mapa) / bottom sheet en mobile (overlay)
 *
 * Dependencias: window.L (cargado desde CDN antes de este script)
 */

class StoreLocator {
  constructor(root) {
    this.root = root;

    // Refs DOM
    this.viewport = root.querySelector('[data-viewport]');
    this.mapEl = root.querySelector('[data-map]');
    this.panel = root.querySelector('[data-panel]');
    this.panelContent = root.querySelector('[data-panel-content]');
    this.panelClose = root.querySelector('[data-panel-close]');
    this.panelHandle = root.querySelector('[data-panel-handle]');
    this.tabs = Array.from(root.querySelectorAll('[data-tab]'));
    this.provinciaSelect = root.querySelector('[data-provincia-select]');
    this.clearBtn = root.querySelector('[data-clear-btn]');
    this.dataItems = Array.from(root.querySelectorAll('[data-store]'));

    // Config desde data attrs
    this.iconOficialUrl = root.dataset.iconOficial;
    this.initialLat = parseFloat(root.dataset.initialLat) || -38.4;
    this.initialLng = parseFloat(root.dataset.initialLng) || -63.6;
    this.initialZoom = parseFloat(root.dataset.initialZoom) || 4;
    this.comoLlegarLabel = root.dataset.comoLlegarLabel || 'Cómo llegar';

    // Estado
    this.activeTab = 'oficial'; // tab por defecto
    this.activeProvincia = '';
    this.stores = [];
    this.markers = [];
    this.map = null;

    this.init();
  }

  init() {
    this.parseStores();

    // Si no hay tiendas con coordenadas válidas, no hacemos nada más
    if (this.stores.length === 0) return;
    if (!this.mapEl) return;

    this.initMap();
    this.createMarkers();
    this.populateProvinces();
    this.bindEvents();
    this.applyFilters();
  }
/**
 * El campo `tipo` es de tipo "Lista" en el metaobject, así que Shopify lo
 * serializa como JSON array (ej: '["oficial"]'). Extraemos el primer valor.
 * Si por alguna razón viene como string plano, lo devolvemos tal cual.
 */
parseListField(raw) {
  const value = String(raw || '').trim();
  if (value.startsWith('[')) {
    try {
      const arr = JSON.parse(value);
      return Array.isArray(arr) && arr[0] ? String(arr[0]).trim() : '';
    } catch (e) {
      return value;
    }
  }
  return value;
}
  /**
   * Normaliza un string de coordenada que puede venir con coma decimal
   * (ej: "-34,50859" ingresado desde el metaobject) a punto decimal estándar.
   */
  parseCoord(raw) {
    return parseFloat(String(raw || '').replace(',', '.'));
  }

  parseStores() {
    this.stores = this.dataItems
      .map((el) => ({
        tipo: this.parseListField(el.dataset.tipo).toLowerCase(),
        provincia: (el.dataset.provincia || '').trim(),
        nombre: el.dataset.nombre || '',
        direccion: el.dataset.direccion || '',
        telefono: el.dataset.telefono || '',
        horario: el.dataset.horario || '',
        lat: this.parseCoord(el.dataset.lat),
        lng: this.parseCoord(el.dataset.lng),
        maps: el.dataset.maps || '',
      }))
      .filter((s) => !isNaN(s.lat) && !isNaN(s.lng));

    // Diagnóstico: log de tipos únicos para verificar valores del metaobject
    const tiposSummary = this.stores.reduce((acc, s) => {
      acc[s.tipo] = (acc[s.tipo] || 0) + 1;
      return acc;
    }, {});
    console.log('[StoreLocator] Tiendas parseadas:', this.stores.length, '| Por tipo:', tiposSummary);
  }

  /**
   * Inicializa Leaflet con tiles de OpenStreetMap.
   * Scroll-zoom desactivado por defecto, se activa al focusear el mapa.
   */
  initMap() {
    this.map = L.map(this.mapEl, {
      center: [this.initialLat, this.initialLng],
      zoom: this.initialZoom,
      scrollWheelZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(this.map);

    // Activar scroll-zoom solo cuando el usuario interactúa con el mapa
    this.map.on('focus', () => this.map.scrollWheelZoom.enable());
    this.map.on('blur', () => this.map.scrollWheelZoom.disable());
    this.mapEl.addEventListener('click', () => this.map.scrollWheelZoom.enable());
    this.mapEl.addEventListener('mouseleave', () => this.map.scrollWheelZoom.disable());
  }

  /**
   * Crea los markers de Leaflet — íconos diferenciados por tipo.
   */
  createMarkers() {
    // Pin custom para tiendas oficiales (Vector_pin.png)
    // Imagen: 108x94px con padding transparente del 25% en cada lado.
    // El padding garantiza que corona, alas y cola se vean completos.
    // iconAnchor: centro horizontal [54], base del ícono visible [94].
    const oficialIcon = L.icon({
      iconUrl: this.iconOficialUrl,
      iconSize: [108, 94],
      iconAnchor: [54, 94],
      popupAnchor: [0, -94],
      className: 'store-locator__marker store-locator__marker--oficial',
    });

    // Pin estándar rojo de Leaflet para revendedores (con paths del CDN)
    const revendedorIcon = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
      className: 'store-locator__marker store-locator__marker--revendedor',
    });

    this.markers = this.stores.map((store) => {
      const icon = store.tipo === 'oficial' ? oficialIcon : revendedorIcon;
      const marker = L.marker([store.lat, store.lng], { icon });
      marker.storeData = store;

      marker.on('click', () => {
        // No abrir panel si el marker está fuera del filtro de provincia
        if (this.activeProvincia && store.provincia !== this.activeProvincia) return;
        this.showStore(store);
      });

      return marker;
    });
  }

  /**
   * Pobla el dropdown de provincias con los valores únicos
   * presentes en los metaobjects (orden alfabético).
   */
  /**
   * Pobla el dropdown con las provincias del tab activo (orden alfabético).
   * Se recarga cada vez que cambia el tab.
   */
  populateProvinces() {
    // Limpiar opciones anteriores excepto el placeholder
    while (this.provinciaSelect.options.length > 1) {
      this.provinciaSelect.remove(1);
    }

    const provinces = [...new Set(
      this.stores
        .filter((s) => s.tipo === this.activeTab)
        .map((s) => s.provincia)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es'));

    provinces.forEach((p) => {
      const opt = document.createElement('option');
      opt.value = p;
      opt.textContent = p;
      this.provinciaSelect.appendChild(opt);
    });
  }

  bindEvents() {
    // Tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activeTab = tab.dataset.tab;
        this.tabs.forEach((t) => {
          const isActive = t === tab;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-selected', String(isActive));
        });
        // Resetear provincia y repoblar dropdown al cambiar de tab
        this.activeProvincia = '';
        this.provinciaSelect.value = '';
        this.populateProvinces();
        this.applyFilters();
      });
    });

    // Dropdown de provincia
    this.provinciaSelect.addEventListener('change', () => {
      this.activeProvincia = this.provinciaSelect.value;
      this.applyFilters();
    });

    // Limpiar filtros
    this.clearBtn.addEventListener('click', () => {
      this.activeProvincia = '';
      this.provinciaSelect.value = '';
      this.applyFilters();
      this.map.setView([this.initialLat, this.initialLng], this.initialZoom);
    });

    // Cierre del panel
    this.panelClose?.addEventListener('click', () => this.closePanel());
    this.panelHandle?.addEventListener('click', () => this.closePanel());

    // ESC cierra panel
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.viewport.classList.contains('is-panel-open')) {
        this.closePanel();
      }
    });

    // Swipe-down en mobile
    this.initSwipeDown();
  }

  /**
   * Aplica los filtros activos (tab + provincia) sobre los markers.
   * - Tab: agrega/remueve markers del mapa según tipo
   * - Provincia: dimea (opacidad 0.25) los markers fuera del filtro
   * - Si hay provincia seleccionada, hace fitBounds sobre los visibles
   */
  applyFilters() {
    // 1. Tab: solo los markers del tipo activo están en el mapa
    this.markers.forEach((m) => {
      const isActiveTab = m.storeData.tipo === this.activeTab;
      if (isActiveTab && !this.map.hasLayer(m)) {
        m.addTo(this.map);
      } else if (!isActiveTab && this.map.hasLayer(m)) {
        this.map.removeLayer(m);
      }
    });

    // 2. Provincia: dimming sobre los markers visibles
    const activeMarkers = this.markers.filter(
      (m) => m.storeData.tipo === this.activeTab
    );
    activeMarkers.forEach((m) => {
      const inProvincia =
        !this.activeProvincia || m.storeData.provincia === this.activeProvincia;
      m.setOpacity(inProvincia ? 1 : 0.25);
    });

    // 3. Si hay provincia seleccionada, zoom a los markers de esa provincia
    if (this.activeProvincia) {
      const filtered = activeMarkers.filter(
        (m) => m.storeData.provincia === this.activeProvincia
      );
      if (filtered.length > 0) {
        const group = L.featureGroup(filtered);
        this.map.fitBounds(group.getBounds(), {
          padding: [40, 40],
          maxZoom: 13,
        });
      }
    }
  }

  /**
   * Abre el panel y carga el contenido de la tienda seleccionada.
   */
  showStore(store) {
    const wasOpen = this.viewport.classList.contains('is-panel-open');
    this.fillPanel(store);
    this.viewport.classList.add('is-panel-open');
    this.panel.setAttribute('aria-hidden', 'false');

    // En desktop, después de la transición de 300ms el mapa cambió de tamaño:
    // hay que invalidarlo para que Leaflet recalcule el viewport.
    if (!wasOpen && window.innerWidth >= 768) {
      setTimeout(() => this.map.invalidateSize({ pan: false }), 320);
    }

    // Centrar el mapa en la tienda seleccionada (sin cambiar zoom)
    this.map.panTo([store.lat, store.lng], { animate: true });
  }

  closePanel() {
    this.viewport.classList.remove('is-panel-open');
    this.panel.setAttribute('aria-hidden', 'true');
    if (window.innerWidth >= 768) {
      setTimeout(() => this.map.invalidateSize({ pan: false }), 320);
    }
  }

  /**
   * Renderiza el HTML del panel a partir de los datos de la tienda.
   */
  fillPanel(store) {
    const esc = (s) => this.escape(s);
    const parts = [];

    parts.push(`<h3 class="store-locator__store-name">${esc(store.nombre)}</h3>`);

    if (store.direccion) {
      const addr = esc(store.direccion);
      const dirHtml = store.maps
        ? `<a href="${esc(store.maps)}" target="_blank" rel="noopener">${addr}</a>`
        : addr;
      parts.push(`
        <div class="store-locator__store-row">
          <span class="store-locator__store-label">Dirección</span>
          <div class="store-locator__store-value">${dirHtml}</div>
        </div>
      `);
    }

    if (store.telefono) {
      const telHref = store.telefono.replace(/\s+/g, '');
      parts.push(`
        <div class="store-locator__store-row">
          <span class="store-locator__store-label">Teléfono</span>
          <div class="store-locator__store-value">
            <a href="tel:${esc(telHref)}">${esc(store.telefono)}</a>
          </div>
        </div>
      `);
    }

    if (store.horario) {
      const horarioHtml = esc(store.horario).replace(/\n/g, '<br>');
      parts.push(`
        <div class="store-locator__store-row">
          <span class="store-locator__store-label">Horario</span>
          <div class="store-locator__store-value">${horarioHtml}</div>
        </div>
      `);
    }

    if (store.maps) {
      parts.push(`
        <a class="store-locator__cta"
           href="${esc(store.maps)}"
           target="_blank"
           rel="noopener">
          ${esc(this.comoLlegarLabel)}
        </a>
      `);
    }

    this.panelContent.innerHTML = parts.join('');
  }

  /**
   * Escape de HTML para evitar inyecciones desde el contenido del metaobject.
   */
  escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str ?? '');
    return div.innerHTML;
  }

  /**
   * Swipe-down para cerrar el bottom sheet en mobile.
   * El gesto se captura solo en el handle (barra superior),
   * para no interferir con el scroll del contenido.
   */
  initSwipeDown() {
    let startY = 0;
    let currentY = 0;
    let dragging = false;

    const onStart = (e) => {
      if (window.innerWidth >= 768) return;
      dragging = true;
      startY = e.touches[0].clientY;
      currentY = startY;
      this.panel.style.transition = 'none';
    };

    const onMove = (e) => {
      if (!dragging) return;
      currentY = e.touches[0].clientY;
      const delta = currentY - startY;
      if (delta > 0) {
        this.panel.style.transform = `translateY(${delta}px)`;
      }
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      this.panel.style.transition = '';
      const delta = currentY - startY;
      if (delta > 80) {
        // Cerrar
        this.panel.style.transform = '';
        this.closePanel();
      } else {
        // Volver a su lugar
        this.panel.style.transform = '';
      }
    };

    this.panelHandle?.addEventListener('touchstart', onStart, { passive: true });
    this.panelHandle?.addEventListener('touchmove', onMove, { passive: true });
    this.panelHandle?.addEventListener('touchend', onEnd, { passive: true });
    this.panelHandle?.addEventListener('touchcancel', onEnd, { passive: true });
  }
}

/* ───── Bootstrap ───── */

const initStoreLocator = (root) => {
  if (root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';
  // eslint-disable-next-line no-new
  new StoreLocator(root);
};

const bootAll = () => {
  // Espera hasta que Leaflet esté disponible (cargado vía CDN con `defer`)
  if (typeof window.L === 'undefined') {
    setTimeout(bootAll, 50);
    return;
  }
  document
    .querySelectorAll('[data-store-locator]')
    .forEach(initStoreLocator);
};

bootAll();

// Soporte para theme editor: re-inicializar cuando se recarga la sección
document.addEventListener('shopify:section:load', (e) => {
  if (typeof window.L === 'undefined') {
    setTimeout(() => {
      e.target
        .querySelectorAll('[data-store-locator]')
        .forEach(initStoreLocator);
    }, 100);
    return;
  }
  e.target
    .querySelectorAll('[data-store-locator]')
    .forEach(initStoreLocator);
});